import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Grid,
    styled,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import { useAppDispatch, useAppSelector } from '../state/hooks';
import { fetchLanes } from '../state/features/lanesSlice';
import { fetchLaneDetails } from '../state/features/laneDetailSlice';
import { ApiStatus, EquipmentTypeCode } from '../utils/enums';
import { buildAreaLink, buildLaneLink } from '../utils/navigationHelpers';
import { parseLaneId } from '../utils/stateHelpers';
import { useFilters } from '../components/Filters/useFilters';
import BasePage from './Layouts/BasePage';
import PageFooter from './Layouts/PageFooter';
import PagePaper from './Layouts/PagePaper';
import PageContent from './Layouts/PageContent';
import PageHeader from './Layouts/PageHeader';
import PageBanner from './Layouts/PageBanner';
import LaneTable from '../components/Tables/LaneTable';
import LaneKPIs from '../components/KPIs/LaneKPIs';
import LaneDetailsHeader from '../components/Details/Headers/LaneDetailHeader';
import LaneDetails from '../components/Details/LaneDetails';
import OrderTable from '../components/Tables/OrderTable';
import LaneMap from '../components/Map/LaneMap';

const EXPAND_TABLE_DURATION = 300;

const classesPrefix = 'lanes';

const classes = {
    transition: `${classesPrefix}-transition`,
    containerExpanded: `${classesPrefix}-containerExpanded`,
    containerShrunk: `${classesPrefix}-containerShrunk`,
    containerHidden: `${classesPrefix}-containerHidden`,
    mapContainer: `${classesPrefix}-mapContainer`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`& .${classes.transition}`]: {
            transitionProperty: 'flex-grow, flex-shrink, flex-basis',
            transitionDuration: `${EXPAND_TABLE_DURATION}ms`,
            transitionTimingFunction: 'ease-in-out',
        },
        [`& .${classes.containerExpanded}`]: {
            flexGrow: 2,
            flexShrink: 1,
            flexBasis: '66.66%',
            [theme.breakpoints.down('lg')]: {
                flexBasis: '50%',
            },
        },
        [`& .${classes.containerShrunk}`]: {
            flexGrow: 1,
            flexShrink: 2,
            flexBasis: '33.33%',
            [theme.breakpoints.down('lg')]: {
                flexBasis: '50%',
            },
        },
        [`& .${classes.containerHidden}`]: {
            flexGrow: 0,
            flexShrink: 1,
            flexBasis: '0%',
        },
        [`& .${classes.mapContainer}`]: {
            display: 'flex',
            flexDirection: 'column',
        },
    };
});

const Lanes = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const filters = useFilters();
    const isMdOrSmaller = useMediaQuery(theme.breakpoints.down('lg'));

    const expandingTableTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [tableExpanding, setTableExpanding] = useState(false);
    const [tableExpanded, setTableExpanded] = useState(false);
    const [hoveredLaneId, setHoveredLaneId] = useState<string | null>(null);
    const [selectedEquipmentType, setSelectedEquipmentType] = useState(EquipmentTypeCode.VN);

    const laneApiStatus = useAppSelector((state) => { return state.lanes.laneApiStatus; });
    const lanes = useAppSelector((state) => { return state.lanes.lanes; });

    const inboundOrdersApiStatus = useAppSelector((state) => { return state.lane.inboundOrdersApiStatus; });
    const inboundOrders = useAppSelector((state) => { return state.lane.inboundOrders; });
    const inboundOrdersCount = useAppSelector((state) => { return state.lane.inboundOrdersCount; });

    const originParam = searchParams.get('origin');
    const destParam = searchParams.get('dest');

    const isLaneActive = useMemo(() => {
        return Boolean(originParam) && Boolean(destParam);
    }, [originParam, destParam]);

    useEffect(() => {
        if (!isLaneActive) {
            setTableExpanded(false);
        }
    }, [isLaneActive]);

    useEffect(() => {
        if (isLaneActive) {
            dispatch(fetchLaneDetails({
                pickup_service_area_id: originParam || null,
                delivery_service_area_id: destParam || null,
                filter_criteria: filters,
            }));
        } else {
            dispatch(fetchLanes({
                filter_criteria: filters,
            }));
        }
    }, [dispatch, originParam, destParam, filters, isLaneActive]);

    useEffect(() => {
        // Clear the timeout when the component unmounts
        return (): void => {
            clearTimeout(expandingTableTimeoutRef.current || undefined);
        };
    }, []);

    const toggleMap = (show: boolean): void => {
        expandingTableTimeoutRef.current = setTimeout(() => {
            setTableExpanding(false);
            setTableExpanded(show);
        }, EXPAND_TABLE_DURATION);

        setTableExpanding(true);
    };

    const handleHoveredLaneChange = (laneId: string | null): void => {
        setHoveredLaneId(laneId);
    };

    const handleLaneClick = (laneId: string): void => {
        const { origin, destination } = parseLaneId(laneId);
        navigate(buildLaneLink(origin, destination, filters));
    };

    const handleAreaClick = (areaId: string): void => {
        navigate(buildAreaLink(areaId, filters));
    };

    if (isLaneActive) {
        return (
            <BasePage>
                <PagePaper>
                    <PageHeader>
                        <LaneDetailsHeader />
                    </PageHeader>
                    <PageContent>
                        <LaneDetails equipmentType={selectedEquipmentType} />
                    </PageContent>
                    <PageFooter>
                        <OrderTable
                            isLoading={inboundOrdersApiStatus === ApiStatus.Idle || inboundOrdersApiStatus === ApiStatus.Loading}
                            orders={inboundOrders}
                            orderCount={inboundOrdersCount}
                            equipmentType={selectedEquipmentType}
                            handleEquipmentTypeChange={(newEquipmentType): void => {
                                setSelectedEquipmentType(newEquipmentType);
                            }}
                        />
                    </PageFooter>
                </PagePaper>
            </BasePage>
        );
    }

    return (
        <BasePage>
            <PageBanner>
                <LaneKPIs />
            </PageBanner>
            <PagePaper>
                <PageContent>
                    <StyledGrid container>
                        <Grid
                            item
                            xs={12}
                            lg={tableExpanded ? 12 : 4}
                            className={`${classes.transition} ${isLaneActive ? classes.containerExpanded : classes.containerShrunk}`}
                        >
                            <LaneTable
                                isExpanded={tableExpanded}
                                adjusting={tableExpanding}
                                hoveredLaneId={hoveredLaneId}
                                handleHoveredLaneChange={handleHoveredLaneChange}
                                toggleTableExpansion={toggleMap}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={tableExpanded ? 'auto' : 8}
                            id='map-container'
                            className={`${classes.transition} ${classes.mapContainer} ${isLaneActive ? classes.containerShrunk : classes.containerExpanded} ${(tableExpanded || isMdOrSmaller) && classes.containerHidden}`}
                        >
                            <LaneMap
                                isFetchingData={laneApiStatus === ApiStatus.Idle || laneApiStatus === ApiStatus.Loading}
                                theme={theme}
                                lanes={lanes}
                                hoveredLaneId={hoveredLaneId}
                                handleHoveredLaneChange={handleHoveredLaneChange}
                                handleLaneClick={handleLaneClick}
                                handleAreaClick={handleAreaClick}
                            />
                        </Grid>
                    </StyledGrid>
                </PageContent>
            </PagePaper>
        </BasePage>
    );
};

export default Lanes;
